/**
 * bridge/review_prompt_controller.js — "review-prompt" bridge component (Stimulus + Strada).
 *
 * Capability-tier bridge component: asks the OS to show its in-app review prompt
 * (iOS StoreKit "Rate us" sheet; Android Play In-App Review). Chippie fires this
 * after the user's 10th invoice is sent. The OS rate-limits the prompt and may
 * show nothing — this is always best-effort, returns no result, and must never
 * block the calling flow.
 *
 * Stimulus identifier "bridge--review-prompt" (glob-derived from this subdir);
 * Strada component name "review-prompt" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/ReviewPromptComponent.swift) and the Android
 * @JavascriptInterface method name (ChippieNativeBridge.requestReview).
 *
 * Why it extends Strada's BridgeComponent: the iOS prompt is reached via
 * this.send("requestReview", ...), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives
 * us that send() transport AND the UA-gated connect() (it only connects in a
 * native shell, so registering the live iOS send is safe).
 *
 * Three-way transport via the window.ChippieBridge facade ({ ios, android, pwa }):
 *   - ios:     this.send("requestReview", {}) over Strada to ReviewPromptComponent.swift
 *   - android: window.ChippieNative.requestReview() (@JavascriptInterface)
 *   - pwa:     no-op. There is no Web API for an OS review prompt; the desktop /
 *              PWA experience drives App/Play Store review through normal web UI,
 *              so this gracefully does nothing rather than faking a prompt.
 *
 * The Android/PWA impls are platform-static (no per-instance state), so register
 * them once at module load via registerStandalone() — callers can fire
 * window.ChippieBridge.requestReview() even with no element on the page (the
 * post-10th-invoice trigger has no anchor element). The controller's connect()
 * upgrades the ios slot to a live `this.send` while inside the iOS shell.
 *
 * Template wiring is optional (most callers fire it programmatically), e.g.:
 *   <span data-controller="bridge--review-prompt"></span>
 * then from JS: window.ChippieBridge.requestReview();
 *
 * Web side follows the sibling share_controller / haptic_controller facade pattern.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Android + PWA impls carry no per-instance state, so register them once at module
// load. The facade is the SSOT for which one runs per platform.
function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("requestReview", {
    // ios is upgraded by the controller's connect() with a real send; this
    // placeholder is a safe no-op if selected before the iOS shell connects.
    ios: () => {},
    android: () => {
      if (window.ChippieNative && window.ChippieNative.requestReview) {
        try { window.ChippieNative.requestReview(); } catch (_e) { /* bridge gone */ }
      }
    },
    // No OS review prompt exists on the web; intentionally a no-op.
    pwa: () => {},
  });
}

// The facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "review-prompt";

  connect() {
    super.connect();
    // Inside the iOS shell now: wire the real Strada send into the facade,
    // keeping the Android/PWA fallbacks intact.
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("requestReview", {
      ios: () => this.send("requestReview", {}, () => {}),
      android: () => {
        if (window.ChippieNative && window.ChippieNative.requestReview) {
          try { window.ChippieNative.requestReview(); } catch (_e) { /* bridge gone */ }
        }
      },
      pwa: () => {},
    });
  }

  disconnect() {
    // Leaving the iOS shell: drop the live-send impl back to the standalone
    // placeholder so a stale `this.send` is never invoked.
    registerStandalone();
    super.disconnect();
  }
}
